/**
 * @fileoverview The server→browser handshake for auth-event ownership.
 * @module features/analytics/serverAuthMarker
 *
 * @description
 * Who emits the signup/login event — the server (an auth callback calling
 * `FlowgridServer.trackAuth`) or the browser (`Flowgrid.setUser` /
 * `completeAuth`) — used to be a rule a customer had to already know, and get
 * right on both ends, or the Acquisition Funnel silently double-counts: a
 * wrong number, not a broken page, so nobody notices for weeks.
 *
 * This closes the loop instead of documenting it. `trackAuth` hands back a
 * short-lived cookie descriptor; the caller sets it (framework-specific — the
 * SDK's server module never assumes it can write the response itself).
 * `Flowgrid.setUser` / `completeAuth` then checks for that cookie before
 * firing its own auth event, and clears it either way so it only ever
 * suppresses the one transition it was written for. An app that passes
 * `authEvents: true` explicitly still wins — this is a default, not a lock.
 */

const COOKIE_NAME = "fg_server_auth_tracked";
// Just long enough to survive an OAuth provider's redirect back to the app;
// short enough that a stale cookie from an abandoned flow can't suppress a
// LATER, unrelated auth event days from now.
const MAX_AGE_SECONDS = 300;

export interface ServerAuthMarkerCookie {
  /** Cookie name — set this, don't invent your own. */
  name: string;
  /** Cookie value — opaque, just needs to be present. */
  value: string;
  /** Suggested `Max-Age` in seconds. */
  maxAge: number;
}

/**
 * What `FlowgridServer.trackAuth` returns for the caller to set.
 *
 * ```ts
 * // Next.js — inside a route handler / server action that CAN write cookies
 * // (an auth *callback* fired as a side effect, e.g. NextAuth's `events`,
 * // usually can't — set it from middleware or the handler that calls
 * // trackAuth instead).
 * const result = await fg.trackAuth({ event: "signup", userId, email });
 * cookies().set(result.marker.name, result.marker.value, {
 *   maxAge: result.marker.maxAge, path: "/", httpOnly: false, sameSite: "lax",
 * });
 * ```
 */
export function serverAuthMarker(): ServerAuthMarkerCookie {
  return { name: COOKIE_NAME, value: "1", maxAge: MAX_AGE_SECONDS };
}

function documentCookies(): string {
  try {
    return typeof document !== "undefined" ? document.cookie : "";
  } catch {
    return "";
  }
}

/** Browser-side: was this transition already tracked server-side? */
export function hasServerAuthMarker(): boolean {
  return documentCookies()
    .split(";")
    .some((part) => part.trim().startsWith(`${COOKIE_NAME}=`));
}

/** Clear the marker so it only ever suppresses the one transition it named. */
export function clearServerAuthMarker(): void {
  try {
    if (typeof document === "undefined") return;
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; path=/`;
  } catch {
    /* noop — privacy mode / sandboxed iframe */
  }
}
